import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, differenceInDays } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,     
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import { filterCrewRoutes } from 'state/helpers';
import {
    countWeightedBHCrewRoutesByField,
    countWeightedCrewRoutesByField,
} from '../../charts/utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import RouteFilterDialog from 'components/RouteFilterDialog';
import RadialBarChart from 'components/chartNivo/RadialBarChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
    { value: "routesByDate", label: "by Date" },
    { value: "routesByPort", label: "by Port" },
    { value: "routesByDays", label: "by Days" },
    { value: "routesBySectors", label: "by Sectors" },
    { value: "routesByACType", label: "by AC Type" },
];

const DailyCrewRouteChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    
    const { startDate, endDate } = useDateRangeContext();
    const dateDiff = differenceInDays(endDate, startDate) + 1;

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    
    const crewRoutesData = useSelector((state) => state.job255CrewRoutes);   
    
    const filteredCrewRoutes = useMemo(() => {
        return filterCrewRoutes({ crewRoutes: crewRoutesData, filters, timeMode });
    }, [crewRoutesData, filters, timeMode]); 
    
    const [view, setView] = useState('routesByDate');
    const [data, setData] = useState({
        bhCrewRoutesByDate: [], 
        bhCrewRoutesByDep: [], 

        bhgroupedRoutesByDays: [],  
        radialBHCrewRoutesByDays: [], 
        bhCrewRoutesByDays: [],   

        bhgroupedRoutesBySectors: [],
        radialBHCrewRoutesBySectors: [], 
        bhCrewRoutesBySectors: [],
        
        bhgroupedRoutesByACType: [],
        radialBHCrewRoutesByACType: [], 
        bhCrewRoutesByACType: [],   
        

        routesByDep: [],
        barLinesRoutesByDep: [],

        groupedRoutesByDays: [],
        routesByDays: [],
        barLinesRoutesByDays: [],

        groupedRoutesBySectors: [],
        routesBySectors: [],
        barLinesRoutesBySectors: [],

        groupedRoutesByACType: [],
        routesByACType: [],
        barLinesRoutesByACType: [],
    });
    
    useEffect(() => {        
        const handleData = async () => {
            try {
                const results = {
                    bhCrewRoutesByDate: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', timeMode === 'UTC' ? 'routeDate' : 'routeDateLocalBase'),  
                    bhCrewRoutesByDep: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'dep'),  

                    bhgroupedRoutesByDays: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'routeDays', 'groupedColumn'),
                    radialBHCrewRoutesByDays: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'routeDays', 'radialbar'),
                    bhCrewRoutesByDays: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'routeDays'),  
                    
                    bhgroupedRoutesBySectors: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'sectors', 'groupedColumn'),
                    radialBHCrewRoutesBySectors: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'routeDays', 'radialbar'),
                    bhCrewRoutesBySectors: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'sectors'),  
                    
                    bhgroupedRoutesByACType: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'acType', 'groupedColumn'),
                    radialBHCrewRoutesByACType: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'routeDays', 'radialbar'),
                    bhCrewRoutesByACType: countWeightedBHCrewRoutesByField(filteredCrewRoutes, 'blockHours', 'acType'),  
                            

                    routesByDep: countWeightedCrewRoutesByField(filteredCrewRoutes, 'dep', 'column', dateDiff),
                    barLinesRoutesByDep: countWeightedCrewRoutesByField(filteredCrewRoutes, 'dep', 'barlines', dateDiff),
                    
                    groupedRoutesByDays: countWeightedCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'groupedColumn', dateDiff),
                    routesByDays: countWeightedCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'column', dateDiff),
                    barLinesRoutesByDays: countWeightedCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'barlines', dateDiff),
                    
                    groupedRoutesBySectors: countWeightedCrewRoutesByField(filteredCrewRoutes, 'sectors', 'groupedColumn', dateDiff),
                    routesBySectors: countWeightedCrewRoutesByField(filteredCrewRoutes, 'sectors', 'column', dateDiff),
                    barLinesRoutesBySectors: countWeightedCrewRoutesByField(filteredCrewRoutes, 'sectors', 'barlines', dateDiff),
                    
                    groupedRoutesByACType: countWeightedCrewRoutesByField(filteredCrewRoutes, 'acType', 'groupedColumn', dateDiff),
                    routesByACType: countWeightedCrewRoutesByField(filteredCrewRoutes, 'acType', 'column', dateDiff),
                    barLinesRoutesByACType: countWeightedCrewRoutesByField(filteredCrewRoutes, 'acType', 'barlines', dateDiff),
                };
                
                setData(results); 
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false); 
            }
        };

        handleData();
    }, [timeMode, filteredCrewRoutes, dateDiff]); 
        
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
    
    const buildOptions = (data, key) =>
        [...new Set(data.map(item => item[key]))].sort();
    
    const { 
        acTypeOptions, 
        flightRegOptions, 
        depOptions, 
        arrOptions 
    } = useMemo(() => ({
        acTypeOptions: buildOptions(crewRoutesData, 'acType'),
        flightRegOptions: buildOptions(crewRoutesData, 'flightReg'),
        depOptions: buildOptions(crewRoutesData, 'dep'),
        arrOptions: buildOptions(crewRoutesData, 'arr'),
    }), [crewRoutesData]);
  
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Daily Routes ${period}`;
    const routesBHTitle = `Daily Routes BH ${period}`;
    const routesCountTitle = `Daily Routes Count ${period}`;
   
    const chartMap = {
        routesByDate: [
            { component: <SortBarMultiLinesChart data={data.bhCrewRoutesByDate} title={routesBHTitle} bottomLegend='Date' leftLegend='Block Hours' barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },            
        ],
        routesByPort: [
            { component: <SortBarMultiLinesChart data={data.bhCrewRoutesByDep} title={routesBHTitle} bottomLegend='Port' leftLegend='Block Hours' barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesByDep} title={routesCountTitle} bottomLegend="Port" leftLegend="Count" digit={1} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Value' digit={1} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
        routesByDays: [
            { component: <SortBarMultiLinesChart data={data.bhCrewRoutesByDays} title={routesBHTitle} bottomLegend='Days' leftLegend='Block Hours' isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <RadialBarChart data={data.radialBHCrewRoutesByDays} title={routesBHTitle} bottomOffSet={5} itemWidthOffSet={-35}  />, style: {} },
            { component: <SortBarChart data={data.bhgroupedRoutesByDays} title={routesBHTitle} bottomLegend='Days' leftLegend='Block Hours' isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },    
            { component: <SortBarChart data={data.groupedRoutesByDays} title={routesCountTitle} bottomLegend="Days" leftLegend="Count" digit={1} isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesByDays} title={routesCountTitle} bottomLegend="Days" leftLegend="Count" digit={1} isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesByDays} title={defaultTitle} bottomLegend='Days' leftLegend='Value' digit={1} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} },         
        ],
        routesBySectors: [
            { component: <SortBarMultiLinesChart data={data.bhCrewRoutesBySectors} title={routesBHTitle} bottomLegend='Sectors' leftLegend='Block Hours' isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <RadialBarChart data={data.radialBHCrewRoutesBySectors} title={routesBHTitle} bottomOffSet={5} itemWidthOffSet={-35}  />, style: {} },
            { component: <SortBarChart data={data.bhgroupedRoutesBySectors} title={routesBHTitle} bottomLegend='Sectors' leftLegend='Block Hours' isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.groupedRoutesBySectors} title={routesCountTitle} bottomLegend="Sectors" leftLegend="Count" digit={1} isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesBySectors} title={routesCountTitle} bottomLegend="Sectors" leftLegend="Count" digit={1} isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesBySectors} title={defaultTitle} bottomLegend='Sectors' leftLegend='Value' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
        routesByACType: [
            { component: <SortBarMultiLinesChart data={data.bhCrewRoutesByACType} title={routesBHTitle} bottomLegend='AC Type' leftLegend='Block Hours' barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <RadialBarChart data={data.radialBHCrewRoutesByACType} title={routesBHTitle} bottomOffSet={5} itemWidthOffSet={-35}  />, style: {} },
            { component: <SortBarChart data={data.bhgroupedRoutesByACType} title={routesBHTitle} bottomLegend='AC Type' leftLegend='Block Hours' isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },          
            { component: <SortBarChart data={data.groupedRoutesByACType} title={routesCountTitle} bottomLegend="AC Type" leftLegend="Count" digit={1} isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesByACType} title={routesCountTitle} bottomLegend="AC Type" leftLegend="Count" digit={1} isShowAvgLine={false} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Value' digit={1} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
    };

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header 
                    title="Daily Route (Job 255)" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '8rem', sm: '9rem' } }}
                        >
                            <InputLabel id="routes-view-label">View</InputLabel>
                            <Select
                                labelId="routes-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                  
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">Error: {error.message}</Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
                >
                    {view in chartMap &&
                        renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
       
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}

            {isFilterDialogOpen &&
                <RouteFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions}
                    flightRegOptions={flightRegOptions}
                    depOptions={depOptions}
                    arrOptions={arrOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default DailyCrewRouteChart;
